import { h } from "../ui/dom";
import { isCorrectWho, normalize } from "../game/answers";
import { WHO_CLUES, whoScore } from "../game/scoring";
import { t } from "../strings";
import type { Playable } from "../game/derive";
import type { Credits } from "../game/knowledge";
import type { Child, Host, RoundModule, RoundState } from "./types";
import { LETTERS, tapToExplain } from "./types";
import { clearLive, liveKey, loadLive, packWho, parseWho, saveLive, type WhoTry } from "./live";
import { journeyOrder } from "../game/route";
import { compareNames, loadAttrs } from "../game/attrs";
import type { NameAttrs } from "../puzzles/types";
import { routeLayers, routeRows } from "./trail";

type P = Extract<Playable, { type: "who" }>;
type Try = WhoTry;
interface S extends RoundState {
  /** Places revealed so far (1–5). */
  shown: number;
  tries: Try[];
  q: string;
  /** Highlighted autocomplete row. */
  hl: number;
  won: boolean;
  /** Storage key of this round's live state (survives a reload). */
  key: string;
  /** Person attributes for the clue line, once loaded (a separate chunk). */
  attrs: Record<string, NameAttrs> | null;
}

/** id of the autocomplete listbox (the input is its combobox). */
const LB = "whoSugg";

const code = (tr: Try) => (tr.ok ? "o" : tr.skip ? "-" : "x");

function credits(p: P, won: boolean): Credits {
  const c: Credits = {};
  p.clues.forEach((_, i) => (c[`c${i}`] = won ? "k" : "l"));
  (p.reveal?.route ?? []).forEach((_, i) => (c[`v${i}`] = "l"));
  return c;
}

function win(host: Host, s: S, p: P, g: string): void {
  clearLive(s.key);
  s.won = true;
  s.tries.push({ g, ok: true });
  host.finish(whoScore(s.shown), { tries: s.tries.map(code), clue: s.shown }, t.who.found(s.shown, p.answer), credits(p, true));
  who.view(host, s, p, 900);
}

/** The clue line for a wrong guess, as shown and as spoken; null until attributes load or when neither name has any. */
function clueLine(s: S, p: P, guess: string): { shown: string; spoken: string } | null {
  const c = s.attrs ? compareNames(s.attrs[guess], s.attrs[p.answer]) : null;
  if (!c) return null;
  const family = p.level === "easy";
  const field = c.field ? t.themes[family ? "family" : "classic"][c.field.field] || null : null;
  return { shown: family ? t.who.clueLineFamily(guess, c) : t.who.clueLine(guess, c, field), spoken: t.who.clueLineSpoken(guess, c, field) };
}

/** One line under each wrong guess comparing it with the answer (time, field, place). */
function clueLines(s: S, p: P): HTMLElement | null {
  const lines = s.tries.filter((x) => !x.skip && !x.ok).flatMap((x) => {
    const l = clueLine(s, p, x.g);
    return l ? [l] : [];
  });
  if (!lines.length) return null;
  return h(
    "ul",
    { class: "clue-lines", "aria-label": t.who.clueLinesLabel, "data-act": "clue-lines" },
    lines.map((l) => h("li", null, h("span", { "aria-hidden": "true" }, l.shown), h("span", { class: "sr-only" }, l.spoken))),
  );
}

/** Wrong guess (`wrong` = the name) or skip (null): reveal another place, or end the round after the fifth. */
function advance(host: Host, s: S, p: P, wrong: string | null): void {
  if (s.shown >= WHO_CLUES) {
    clearLive(s.key);
    s.won = false;
    host.finish(0, { tries: s.tries.map(code), clue: 0 }, t.who.itWas(p.answer), credits(p, false));
    who.view(host, s, p, 900);
    return;
  }
  s.shown++;
  saveLive(s.key, packWho(s));
  const c = p.clues[s.shown - 1]!;
  const place = t.who.placeAnnounce(s.shown, c.name, p.hideYears ? null : (c.when ?? null));
  host.toast(wrong ? t.who.not(wrong) : t.who.anotherPlace);
  const line = wrong ? clueLine(s, p, wrong) : null;
  host.announce(wrong ? `${t.who.notAnnounce(wrong)}${line ? ` ${line.spoken}` : ""} ${place}` : place);
  host.draw();
  who.view(host, s, p, 700);
  setTimeout(() => {
    const el = document.getElementById("whoInput");
    if (el && matchMedia("(hover: hover)").matches) el.focus();
  }, 50);
}

/** Places revealed so far, newest last — the panel's text version of the map (no step notes while guessing). */
function clueList(s: S, p: P): HTMLElement {
  return h(
    "ol",
    { class: "clues", "aria-label": t.who.cluesLabel },
    p.clues.slice(0, s.shown).map((c) => h("li", null, h("b", null, c.name), !p.hideYears && c.when ? h("small", null, ` · ${c.when}`) : null)),
  );
}

const coarse = (): boolean => typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;

/**
 * Touch: act on the tap itself (touchend on the element where it started) rather than the click that follows,
 * because closing the on-screen keyboard can move the button before the click arrives. A drag isn't a tap.
 * Mouse and keyboard still use `click`.
 */
function onTap<T extends HTMLElement>(el: T, fn: () => void): T {
  let start: { x: number; y: number } | null = null;
  el.addEventListener("touchstart", (e) => {
    const p = e.touches[0];
    start = p ? { x: p.clientX, y: p.clientY } : null;
  }, { passive: true });
  el.addEventListener("touchend", (e) => {
    const p = e.changedTouches[0];
    if (!start || !p || Math.hypot(p.clientX - start.x, p.clientY - start.y) > 12) return;
    start = null;
    e.preventDefault(); // no synthetic click afterwards, so the guess isn't sent twice
    fn();
  });
  return el;
}

export const who: RoundModule<P, S> = {
  init: (p, host) => {
    const key = liveKey(host.level, p.id);
    const live = loadLive(key, parseWho);
    const s: S = {
      done: false,
      shown: live?.shown ?? 1,
      tries: live?.tries ?? [],
      q: "",
      hl: -1,
      won: false,
      key,
      attrs: null,
    };
    // The clue line needs the attribute table; fetch it now, redraw if wrong guesses are already on screen (reload).
    void loadAttrs().then((a) => {
      s.attrs = a;
      if (!s.done && s.tries.some((x) => !x.skip)) host.draw();
    });
    return s;
  },

  view(host, s, p, ms) {
    const pts = s.done ? p.clues : p.clues.slice(0, s.shown);
    host.map.fitPoints(pts, {
      ms,
      maxWorldPx: pts.length === 1 ? 5000 : 12000,
    });
  },

  render(host, s, p) {
    const C = host.map.colors;
    const n = s.done ? p.clues.length : s.shown;
    // After the round: the whole life as a route, oldest first, every stop with its year and note (the clues are
    // already in journey order, see derive.ts).
    host.map.set(
      s.done
        ? { ...routeLayers({ stops: p.clues, color: s.won ? C.good : C.accent, idBase: 0, line: journeyOrder(p.clues).dated }), cities: true }
        : {
            pins: p.clues.slice(0, n).map((c, i) => ({
              ...c,
              text: String(i + 1),
              color: C.accent,
              label: c.name,
              sub: p.hideYears ? null : (c.when ?? null),
              z: i === n - 1 ? 3 : 2,
              halo: i === n - 1,
            })),
            cities: true,
          },
    );
    host.map.onPinClick = null;
    if (s.done) tapToExplain(host, p.clues);
    host.map.onMapClick = null;
    host.map.setPicking(false);
    const pts = whoScore(s.shown);
    const kids: Child[] = [h("p", { class: "clue" }, p.something ? t.who.introThing : t.who.introPerson)];
    const kicker = h("div", { class: "kicker" }, h("span", null, t.who.placeOf(s.shown)), h("span", null, t.who.worth(pts)));

    const skipRow = h(
      "div",
      { class: "row" },
      h(
        "button",
        {
          class: "btn ghost",
          type: "button",
          "data-act": "skip",
          onclick: () => {
            s.tries.push({ skip: true, g: "" });
            advance(host, s, p, null);
          },
        },
        s.shown < WHO_CLUES ? t.who.another(s.shown + 1, whoScore(s.shown + 1)) : t.who.giveUp,
      ),
    );

    if (s.done) {
      kids.push(
        routeRows(p.clues, { kind: "who" }),
        p.clues.some((c) => c.note) ? h("p", { class: "hint" }, t.ui.tapPins) : null,
        host.verdict(),
      );
    } else if (p.choices) {
      // Easy: pick from four
      const wrong = new Set(s.tries.map((x) => x.g));
      const pick = (name: string) => {
        if (name === p.answer) return win(host, s, p, name);
        s.tries.push({ g: name });
        advance(host, s, p, name);
      };
      kids.push(
        kicker,
        clueList(s, p),
        h(
          "div",
          { class: "opts" },
          p.choices.map((name, i) =>
            h(
              "button",
              {
                class: `opt${wrong.has(name) ? " bad" : ""}`,
                type: "button",
                "data-act": "choice",
                "data-key": `choice-${i}`,
                "aria-label": wrong.has(name) ? t.who.wrongGuess(name) : null,
                disabled: wrong.has(name),
                onclick: () => pick(name),
              },
              h("span", { class: "lt", "aria-hidden": "true" }, wrong.has(name) ? "✕" : LETTERS[i]),
              h("span", { class: "tx" }, name),
            ),
          ),
        ),
        clueLines(s, p),
        skipRow,
      );
    } else {
      // Medium/Hard: type a name, with autocomplete (ARIA 1.2 combobox + listbox)
      const input = h("input", {
        id: "whoInput",
        type: "text",
        role: "combobox",
        "aria-autocomplete": "list",
        "aria-expanded": "false",
        "aria-controls": LB,
        "aria-label": t.who.ariaGuess,
        "data-act": "guess-input",
        "data-key": "who-input",
        placeholder: t.who.placeholder,
        autocomplete: "off",
        spellcheck: "false",
        value: s.q,
      });
      const sugg = h("div", {
        id: LB,
        class: "sugg",
        hidden: true,
        role: "listbox",
        "aria-label": t.who.suggestions,
      });
      let list: string[] = [];
      const matches = () => {
        const q = normalize(input.value);
        return q.length < 2 ? [] : host.names.filter((nm) => normalize(nm).includes(q)).slice(0, 6);
      };
      const close = () => {
        list = [];
        s.hl = -1;
        sugg.hidden = true;
        sugg.replaceChildren();
        input.setAttribute("aria-expanded", "false");
        input.removeAttribute("aria-activedescendant");
      };
      const choose = (nm: string) => {
        input.value = nm;
        s.q = nm;
        close();
        // On a touch screen, close the keyboard once a name is picked: if it stays open, the first tap on Guess
        // only dismisses it (the page shifts under the finger) and a second tap is needed (Egor, iPhone, 30 Sep).
        if (coarse()) input.blur();
        else input.focus();
      };
      const paint = () => {
        list = matches();
        if (!list.length) return close();
        if (s.hl >= list.length) s.hl = -1;
        sugg.replaceChildren(
          ...list.map((nm, i) =>
            h(
              "button",
              {
                type: "button",
                role: "option",
                id: `${LB}-${i}`,
                tabindex: "-1",
                "aria-selected": String(i === s.hl),
                class: i === s.hl ? "on" : "",
                "data-key": `sugg-${i}`,
                // keep focus in the input so the list stays open; pick on click (mouse, touch, pen)
                onmousedown: (e: Event) => e.preventDefault(),
                onclick: () => choose(nm),
              },
              nm,
            ),
          ),
        );
        sugg.hidden = false;
        input.setAttribute("aria-expanded", "true");
        if (s.hl >= 0) input.setAttribute("aria-activedescendant", `${LB}-${s.hl}`);
        else input.removeAttribute("aria-activedescendant");
      };
      const guess = () => {
        const g = input.value.trim();
        if (!g) return;
        close();
        if (isCorrectWho(g, p)) return win(host, s, p, g);
        // Only names from the list count as guesses: anything else (a typo, half a name) costs no step.
        const known = host.names.find((nm) => normalize(nm) === normalize(g));
        if (!known) {
          host.toast(t.who.pickFromList);
          host.announce(t.who.pickFromList);
          return;
        }
        s.tries.push({ g: known });
        s.q = "";
        advance(host, s, p, known);
      };
      input.addEventListener("input", () => {
        s.q = input.value;
        s.hl = -1;
        paint();
      });
      input.addEventListener("blur", () => setTimeout(() => document.activeElement !== input && close(), 150));
      input.addEventListener("keydown", (e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          if (sugg.hidden) {
            s.hl = -1;
            paint();
          }
          if (!list.length) return;
          e.preventDefault();
          s.hl = e.key === "ArrowDown" ? (s.hl + 1) % list.length : s.hl <= 0 ? list.length - 1 : s.hl - 1;
          paint();
        } else if (e.key === "Enter") {
          e.preventDefault();
          const nm = s.hl >= 0 && !sugg.hidden ? list[s.hl] : undefined;
          if (nm) choose(nm);
          else guess();
        } else if (e.key === "Escape" && !sugg.hidden) {
          e.preventDefault();
          close();
        }
      });
      kids.push(
        p.hideYears ? h("p", { class: "clue how" }, t.who.hardHint) : null,
        kicker,
        clueList(s, p),
        h(
          "div",
          { class: "who-in" },
          input,
          sugg,
          onTap(
            h(
              "button",
              {
                class: "btn",
                type: "button",
                "data-act": "submit",
                // Keep focus in the input while the button is pressed: losing it grows the map back (it shrinks
                // while typing, see app.css) and the button jumps away before the click lands.
                onpointerdown: (e: Event) => e.preventDefault(),
                onmousedown: (e: Event) => e.preventDefault(),
                onclick: guess,
              },
              t.who.guess,
            ),
            guess,
          ),
        ),
        s.tries.length
          ? h(
              "div",
              { class: "tries", role: "list" },
              s.tries.map((x) =>
                h(
                  "span",
                  {
                    class: x.skip ? "skip" : "",
                    role: "listitem",
                    "aria-label": x.skip ? t.who.skippedLabel : t.who.wrongGuess(x.g),
                  },
                  x.skip ? t.who.skipped : x.g,
                ),
              ),
            )
          : null,
        clueLines(s, p),
        skipRow,
      );
    }
    host.frame(p, ...kids);
  },
};
